import { newRequestId } from '../utils/request-id';

/**
 * THE SIGN OPERATION — one click-wrap acceptance, however many clicks it takes.
 *
 * ── WHAT WENT WRONG ────────────────────────────────────────────────────────
 * A live QA sweep (2026-08-05, QA-08-05-05) double-clicked `Sign and enroll` and
 * observed **two `POST /bff/sign/accept` requests carrying two DIFFERENT
 * `Idempotency-Key` values**. Two distinct keys mean the BFF's idempotency layer is
 * never consulted for the second request — by construction it is a SECOND
 * OPERATION. Exactly one contract was created, but only because Quotation's state
 * machine refused a second accept on an already-signed quote. The defence that is
 * supposed to be enforcing was not the one that held: the client was relying on a
 * server-side invariant it does not own, in another repository, that could be
 * relaxed without anyone here noticing.
 *
 * ── WHY A SHARED KEY ALONE WOULD NOT HAVE FIXED IT ─────────────────────────
 * ⚠️ THE PAYLOAD WAS ALSO DIFFERENT PER CLICK. `buildSignatureEvent` stamps
 * `clickwrap.acceptedAt` from a `new Date()` taken at click time, so two clicks a
 * second apart produce two different bodies. The BFF's idempotency rule is
 * "same key + same body → cached replay; same key + DIFFERENT body → 409
 * idempotency-key-conflict" — so forcing one key onto two bodies would have traded
 * a duplicate-risk for a guaranteed 409 in the customer's face.
 *
 * Both halves therefore belong to the OPERATION, not to the request:
 *   - `acceptedAt` — the instant the customer accepted is the FIRST click. That is
 *     also the more truthful record: the second click is not a second acceptance.
 *   - `idempotencyKey` — minted once, reused for every retry/replay.
 *
 * Two clicks now produce an identical body under an identical key, so the BFF
 * replays its cached answer and the idempotency layer is doing the work it exists
 * to do.
 *
 * ── WHEN AN OPERATION ENDS ─────────────────────────────────────────────────
 *   - `settleSignOperation` — the submit came back (either way). The operation is
 *     KEPT, so a retry after a failure reuses the same key and the same acceptance
 *     instant. That is what "reused for every retry of that same operation" means.
 *   - `resetSignOperation` — the customer changed the terms (a date, a consent, the
 *     SMS opt-in). Those are genuinely different terms, so it is a different
 *     acceptance and needs a fresh key; reusing the old one would 409.
 *
 * ── THE IN-FLIGHT CLAIM ────────────────────────────────────────────────────
 * `claimSignOperation` refuses while a submit is in flight. The submit button's
 * `disabled` state is necessary but NOT sufficient — two clicks inside one React
 * tick both read the pre-commit `isPending: false`, which is how QA got two
 * requests in the first place. This is the deterministic half of that guard.
 *
 * Pure: every instant and every key is injected, so the tests pin exact values.
 */

export interface SignOperation {
  /** The click-wrap acceptance instant — minted when the operation BEGAN. */
  readonly acceptedAt: Date;
  /** The `Idempotency-Key` carried by every request belonging to this operation. */
  readonly idempotencyKey: string;
}

export interface SignOperationDeps {
  readonly now: () => Date;
  readonly newKey: () => string;
}

/** Real clock + real UUID. Overridden in tests. */
export const defaultSignOperationDeps: SignOperationDeps = {
  now: () => new Date(),
  newKey: newRequestId,
};

export interface SignOperationState {
  /** The open operation, or `undefined` before the first submit / after a reset. */
  readonly operation: SignOperation | undefined;
  /** True between a claim and its settle. */
  readonly inFlight: boolean;
}

/** No operation open, nothing in flight. */
export const idleSignOperation: SignOperationState = { operation: undefined, inFlight: false };

export interface SignOperationClaim {
  readonly state: SignOperationState;
  /**
   * The operation to submit — `undefined` when one is already in flight, which the
   * caller must treat as "do nothing", not as an error.
   */
  readonly claimed: SignOperation | undefined;
}

/**
 * Claim the operation for a submit: resume the open one, or begin a fresh one.
 * Refuses (returns `claimed: undefined`, state untouched) while a submit is in
 * flight.
 */
export function claimSignOperation(
  state: SignOperationState,
  deps: SignOperationDeps = defaultSignOperationDeps,
): SignOperationClaim {
  if (state.inFlight) return { state, claimed: undefined };

  const operation: SignOperation =
    state.operation ?? { acceptedAt: deps.now(), idempotencyKey: deps.newKey() };

  return { state: { operation, inFlight: true }, claimed: operation };
}

/**
 * The submit settled. The operation is deliberately KEPT: a retry after a failure
 * is the same acceptance and must carry the same key and the same instant.
 */
export function settleSignOperation(state: SignOperationState): SignOperationState {
  return { operation: state.operation, inFlight: false };
}

/**
 * The customer changed the terms — end the operation so the next submit is a new
 * acceptance with a new key.
 */
export function resetSignOperation(): SignOperationState {
  return idleSignOperation;
}
